import React, { type ComponentProps } from 'react';

import { VerifiedTeamIcon } from '@atlaskit/people-teams-ui-public/verified-team-icon';
import { Inline, Stack } from '@atlaskit/primitives/compiled';

import { useShowBadges } from '../../hooks/useShowBadges';

import { ArchivedLozenge } from './ArchivedLozenge';
import { Description } from './Description';
import { TeamName } from './TeamName';
import { TeamType } from './TeamType';

type TeamContentProps = Pick<ComponentProps<typeof Description>, 'description'> &
	Pick<ComponentProps<typeof TeamName>, 'teamName'> &
	Pick<ComponentProps<typeof TeamType>, 'teamType'> & {
		showType?: boolean;
	};

/**
 * Internal component that renders the team name and optional secondary text.
 * Handles truncation detection and conditionally displays verified/archived badges.
 */
function TeamContent({
	teamName,
	description,
	teamType,
	showType = false,
}: TeamContentProps): React.JSX.Element | null {
	const shouldShowType = Boolean(showType && teamType);
	const {
		elementRef: teamNameRef,
		showVerified: showVerifiedWithName,
		showArchived,
	} = useShowBadges({
		showVerifiedBadge: !shouldShowType,
		showArchivedBadge: true,
	});
	const { elementRef: teamTypeRef, showVerified: showVerifiedWithType } = useShowBadges({
		showVerifiedBadge: shouldShowType,
		showArchivedBadge: false,
	});

	if (!teamName && !description && !shouldShowType) {
		return null;
	}

	return (
		<Stack>
			{teamName ? (
				<Inline alignBlock="center" space="space.025">
					<TeamName teamName={teamName} elementRef={teamNameRef} />
					{showVerifiedWithName ? <VerifiedTeamIcon /> : null}
					{showArchived ? <ArchivedLozenge /> : null}
				</Inline>
			) : null}
			{shouldShowType ? (
				<TeamType
					teamType={teamType}
					elementRef={teamTypeRef}
					showVerified={showVerifiedWithType}
				/>
			) : (
				<Description description={description} />
			)}
		</Stack>
	);
}

export { TeamContent };
